import { createFileRoute } from '@tanstack/react-router';
import { CalendarDays, Filter, Play } from 'lucide-react';
import { Card } from '@/components/Card/Card';
import type {
  IRelatorioGiroEstoqueFiltros,
  IRelatorioGiroEstoqueFiltrosAvancados,
} from '@/types/relatorioGiroEstoque';
import './giro-estoque.css';

export const Route = createFileRoute('/_authenticated/relatorio/giro-estoque/form')({
  component: RelatorioGiroEstoqueFormPage,
});

export interface IRelatorioGiroEstoqueFormProps {
  filtros: IRelatorioGiroEstoqueFiltros;
  isGenerating?: boolean;
  errorMessage?: string;
  onFiltrosChange: (filtros: IRelatorioGiroEstoqueFiltros) => void;
  onGenerate: () => void;
}

export function RelatorioGiroEstoqueForm({
  filtros,
  isGenerating = false,
  errorMessage,
  onFiltrosChange,
  onGenerate,
}: IRelatorioGiroEstoqueFormProps) {
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    onFiltrosChange({
      ...filtros,
      [name]: value,
    });
  };

  return (
    <Card className="filter relatorio-giro-estoque-params-card">
      <Card.Header>
        <div className="relatorio-giro-estoque-section-header">
          <CalendarDays
            size={18}
            aria-hidden="true"
          />
          <h3>Parâmetros do Relatório</h3>
        </div>
      </Card.Header>

      <Card.Content className="filter-card__content">
        <div className="relatorio-giro-estoque-params-grid">
          <div className="input-content">
            <label htmlFor="dataInicio">Data Início</label>
            <input
              type="date"
              id="dataInicio"
              name="dataInicio"
              value={filtros.dataInicio}
              onChange={handleInputChange}
            />
          </div>

          <div className="input-content">
            <label htmlFor="dataFim">Data Fim</label>
            <input
              type="date"
              id="dataFim"
              name="dataFim"
              value={filtros.dataFim}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <button
          type="button"
          className="filter-button relatorio-giro-estoque-generate-btn"
          onClick={onGenerate}
          disabled={isGenerating}
        >
          <Play
            size={16}
            aria-hidden="true"
          />
          {isGenerating ? 'Gerando...' : 'Gerar Relatório'}
        </button>

        {errorMessage && (
          <div className="relatorio-giro-estoque-message relatorio-giro-estoque-message--error">
            {errorMessage}
          </div>
        )}
      </Card.Content>
    </Card>
  );
}

export interface IRelatorioGiroEstoqueFiltrosFormProps {
  filtrosAvancados: IRelatorioGiroEstoqueFiltrosAvancados;
  isGenerating?: boolean;
  onFiltrosAvancadosChange: (filtros: IRelatorioGiroEstoqueFiltrosAvancados) => void;
  onApply: () => void;
  onClear: () => void;
}

export function RelatorioGiroEstoqueFiltrosForm({
  filtrosAvancados,
  isGenerating = false,
  onFiltrosAvancadosChange,
  onApply,
  onClear,
}: IRelatorioGiroEstoqueFiltrosFormProps) {
  const handleFiltroChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    onFiltrosAvancadosChange({
      ...filtrosAvancados,
      [name]: value,
    });
  };

  return (
    <Card className="filter relatorio-giro-estoque-filtros-card">
      <Card.Header>
        <div className="relatorio-giro-estoque-section-header">
          <Filter
            size={18}
            aria-hidden="true"
          />
          <h3>Filtros e Ordenação</h3>
        </div>
      </Card.Header>

      <Card.Content className="filter-card__content">
        <div className="relatorio-giro-estoque-filtros-grid">
          <div className="input-content">
            <label htmlFor="filtroTexto">Código/Descrição</label>
            <input
              type="text"
              id="filtroTexto"
              name="filtroTexto"
              value={filtrosAvancados.filtroTexto}
              onChange={handleFiltroChange}
              placeholder="Ex: SKU-001..."
            />
          </div>

          <div className="input-content">
            <label htmlFor="filtroMinMovimentacao">Movimentação (Mín/Máx)</label>
            <div className="relatorio-giro-estoque-range-inputs">
              <input
                type="number"
                id="filtroMinMovimentacao"
                name="filtroMinMovimentacao"
                value={filtrosAvancados.filtroMinMovimentacao}
                onChange={handleFiltroChange}
                placeholder="0"
              />
              <input
                type="number"
                id="filtroMaxMovimentacao"
                name="filtroMaxMovimentacao"
                value={filtrosAvancados.filtroMaxMovimentacao}
                onChange={handleFiltroChange}
                placeholder="999k"
              />
            </div>
          </div>

          <div className="input-content">
            <label htmlFor="filtroMinEstoque">Estoque (Mín/Máx)</label>
            <div className="relatorio-giro-estoque-range-inputs">
              <input
                type="number"
                id="filtroMinEstoque"
                name="filtroMinEstoque"
                value={filtrosAvancados.filtroMinEstoque}
                onChange={handleFiltroChange}
                placeholder="0"
              />
              <input
                type="number"
                id="filtroMaxEstoque"
                name="filtroMaxEstoque"
                value={filtrosAvancados.filtroMaxEstoque}
                onChange={handleFiltroChange}
                placeholder="999k"
              />
            </div>
          </div>

          <div className="input-content">
            <label htmlFor="filtroMinDiasSemSaida">Dias sem Saída (Mín/Máx)</label>
            <div className="relatorio-giro-estoque-range-inputs">
              <input
                type="number"
                id="filtroMinDiasSemSaida"
                name="filtroMinDiasSemSaida"
                value={filtrosAvancados.filtroMinDiasSemSaida}
                onChange={handleFiltroChange}
                placeholder="0"
              />
              <input
                type="number"
                id="filtroMaxDiasSemSaida"
                name="filtroMaxDiasSemSaida"
                value={filtrosAvancados.filtroMaxDiasSemSaida}
                onChange={handleFiltroChange}
                placeholder="365"
              />
            </div>
          </div>
        </div>

        <div className="relatorio-giro-estoque-filtros-actions">
          <button
            type="button"
            className="export-button"
            onClick={onClear}
          >
            Limpar Filtros
          </button>
          <button
            type="button"
            className="filter-button"
            onClick={onApply}
            disabled={isGenerating}
          >
            Aplicar Filtros
          </button>
        </div>
      </Card.Content>
    </Card>
  );
}

function RelatorioGiroEstoqueFormPage() {
  return (
    <div className="relatorio-giro-estoque-container">
      <div className="relatorio-giro-estoque-content">
        <p className="relatorio-giro-estoque-message relatorio-giro-estoque-message--info">
          Utilize a listagem principal para gerar e exportar o relatório.
        </p>
      </div>
    </div>
  );
}
